import React, { useEffect, useState } from 'react'

import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '@/ui/atoms/dialog/Dialog'
import { FormControl } from '@/ui/atoms/form/Form'
import { TokenIcon } from '@/ui/atoms/token-icon/TokenIcon'
import { Typography } from '@/ui/atoms/typography/Typography'
import { cn } from '@/ui/utils/style'
import { testIds } from '@/ui/utils/testIds'
import { getDisplayToken } from '@/domain/hooks/useDisplayToken'
import { useChainId } from 'wagmi'
import { getChainConfigEntry } from '@/config/chain'
import { SearchInput } from '@/ui/atoms/search-input/SearchInput'
import { normalizeForSearch } from '@/utils/strings'
import { ChevronDown } from 'lucide-react'
import { L1Badge } from '../l1-badge/L1Badge'

const HEADER_HEIGHT = 148
const LIST_FALLBACK_HEIGHT = 300
const DEBOUNCE_TIME = 200

interface AssetWithBalances {
  token: Token
  balance?: NormalizedUnitNumber
  l1Balance?: NormalizedUnitNumber
}

interface SearchableAssetSelectorProps {
  assets: AssetWithBalances[]
  selectedAsset?: Token
  setSelectedAsset?: (newSymbol: TokenSymbol) => void
  withFormControl?: boolean
  disabled?: boolean
  resetBorrowStatus?: () => void
  isL1?: boolean
  swapMode?: string
  isDestination?: boolean
  featuredAssets?: string[]
}

export function SearchableAssetSelector({
  assets,
  selectedAsset,
  setSelectedAsset,
  withFormControl,
  disabled,
  resetBorrowStatus,
  isL1,
  swapMode,
  isDestination,
  featuredAssets,
}: SearchableAssetSelectorProps) {
  const chainId = useChainId()
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const Wrapper = withFormControl ? FormControl : React.Fragment
  const { nativeAssetInfo } = getChainConfigEntry(chainId).markets || {}
  const [listHeight, setListHeight] = useState(0)
  const readonly = assets.length <= 1

  const filteredAssets = assets.filter((asset) => {
    const normalizedSymbol = normalizeForSearch(asset.token.symbol)
    const normalizedName = normalizeForSearch(asset.token.name)
    const normalizedQuery = normalizeForSearch(searchQuery)

    return normalizedSymbol.includes(normalizedQuery) || normalizedName.includes(normalizedQuery)
  })

  function handleSelect(symbol: string) {
    if (disabled && resetBorrowStatus) {
      resetBorrowStatus()
    }
    setSelectedAsset?.(TokenSymbol(symbol))
    setOpen(false)
  }

  function handleOnOpenChange(open: boolean) {
    setOpen(open)
    setSearchQuery('')
  }

  // Ensure scrollable list takes into account mobile keyboard height
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null

    function handleResize() {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
      timeoutId = setTimeout(() => {
        const visibleHeight = window.visualViewport?.height || window.innerHeight
        setListHeight(visibleHeight - HEADER_HEIGHT)
      }, DEBOUNCE_TIME)
    }

    if (open) {
      const visibleHeight = window.visualViewport?.height || window.innerHeight
      setListHeight(visibleHeight - HEADER_HEIGHT)
      window.visualViewport?.addEventListener('resize', handleResize)
      return () => {
        window.visualViewport?.removeEventListener('resize', handleResize)
        if (timeoutId) clearTimeout(timeoutId)
      }
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [open])

  const displaySelectedAsset = selectedAsset
    ? getDisplayToken(chainId, selectedAsset, { skipWrappedNative: true })
    : undefined

  return (
    <Dialog open={open} onOpenChange={handleOnOpenChange}>
      <DialogTrigger
        className={cn(
          'flex h-14 w-36 items-center justify-between rounded-sm border border-input bg-input px-4 py-4',
          'ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
          disabled && 'cursor-not-allowed opacity-70',
          readonly && 'pointer-events-none',
        )}
        disabled={(disabled && !resetBorrowStatus) || readonly}
        data-testid={testIds.component.AssetSelector.trigger}
        aria-label="Asset selector"
      >
        <Wrapper>
          <div className="flex flex-row items-center gap-2">
            {selectedAsset && (
              <div className="relative">
                <TokenIcon token={selectedAsset} className="h-6 w-6" />
                {isL1 && <L1Badge />}
              </div>
            )}
            <Typography className="text-foreground">{displaySelectedAsset?.symbol}</Typography>
          </div>
        </Wrapper>
        {!readonly && <ChevronDown className="h-5 w-5 text-icon-foreground" />}
      </DialogTrigger>
      <DialogContent
        className="md:data-[state=closed]:slide-out-to-top-[16px] md:data-[state=open]:slide-in-from-top-[16px] border border-border bg-background pt-6 md:top-24 md:min-w-0 md:max-w-md md:pb-2"
        aria-describedby={undefined}
        contentVerticalPosition="top"
        overlayClassName="z-[240]"
        closeClassName="top-6"
      >
        <div className="mb-4">
          <DialogTitle>
            <Typography variant="p" className="font-bold text-lg" gradient>
              Select token
            </Typography>
          </DialogTitle>
          <SearchInput
            value={searchQuery}
            placeholder="Search tokens..."
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div
          className="space-y-2 overflow-y-auto md:max-h-80"
          style={{ height: Math.max(listHeight, LIST_FALLBACK_HEIGHT) }}
        >
          {!!featuredAssets?.length && (
            <>
              <Typography variant="p" className="mb-3 font-semibold">
                Featured
              </Typography>

              <div className="my-4 flex flex-row flex-wrap gap-2">
                {featuredAssets
                  .flatMap((symbol) => assets.filter((a) => a.token.symbol === symbol))
                  .map((a) => {
                    const displayToken =
                      a.token.symbol === nativeAssetInfo?.wrappedNativeAssetSymbol
                        ? a.token
                        : getDisplayToken(chainId, a.token)

                    return (
                      <button
                        key={`featured-${a.token.symbol}`}
                        onClick={() => handleSelect(a.token.symbol)}
                        className="flex flex-row items-center gap-2 rounded-full bg-accent p-1 pr-3 hover:bg-muted"
                      >
                        <TokenIcon token={displayToken} className="h-5 w-5" />
                        <Typography className="font-bold text-foreground text-sm">{displayToken.symbol}</Typography>
                      </button>
                    )
                  })}
              </div>
            </>
          )}

          <Typography variant="p" className="pt-3 font-semibold">
            Tokens
          </Typography>

          {filteredAssets.map((asset) => {
            const displayToken =
              asset.token.symbol === nativeAssetInfo?.wrappedNativeAssetSymbol
                ? asset.token
                : getDisplayToken(chainId, asset.token)

            const balance = getAssetInfo({
              swapMode,
              isDestination,
              asset,
            })

            return (
              <button
                key={`${asset.token.address}-${asset.token.symbol}`}
                className="flex w-full items-center rounded-sm p-3 transition-colors hover:bg-muted"
                onClick={() => handleSelect(asset.token.symbol)}
                data-testid={testIds.component.AssetSelector.option}
              >
                <div className={cn('flex w-full flex-row justify-between', asset.balance && 'w-full')}>
                  <div className="flex flex-row items-center gap-3">
                    <div className="relative">
                      <TokenIcon token={displayToken} className="h-6 w-6" />
                      {isL1 && <L1Badge />}
                    </div>
                    <div className="flex flex-col text-left">
                      <Typography className="font-semibold">{displayToken.name}</Typography>
                      <Typography variant="prompt">{displayToken.symbol}</Typography>
                    </div>
                  </div>
                  <div className="flex flex-row items-center">
                    <div className="flex flex-col items-end gap-1">
                      {asset.token.unitPriceUsd.gt(0) && balance.gt(0) && (
                        <Typography className="text-sm">{asset.token.formatUSD(balance)}</Typography>
                      )}

                      {balance.gt(0) && (
                        <Typography variant="prompt">{asset.token.format(balance, { style: 'compact' })}</Typography>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            )
          })}
          {filteredAssets.length === 0 && (
            <div className="p-4 text-center">
              <Typography variant="prompt">No assets found</Typography>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

function getAssetInfo({
  swapMode,
  isDestination,
  asset,
}: {
  swapMode?: string
  isDestination?: boolean
  asset: AssetWithBalances
}) {
  if (asset.balance && swapMode === 'bridgeToHyperEVM' && isDestination) {
    return asset.balance
  }

  if (
    asset.balance &&
    !isDestination &&
    swapMode !== 'bridgeToHyperEVM' &&
    swapMode !== 'buyHyperCore' &&
    swapMode !== 'sellHyperCore'
  ) {
    return asset.balance
  }

  if (
    asset.l1Balance &&
    isDestination &&
    swapMode !== 'bridgeToHyperEVM' &&
    swapMode !== 'buyHyperCore' &&
    swapMode !== 'sellHyperCore'
  ) {
    return asset.l1Balance
  }

  if (asset.l1Balance && swapMode === 'bridgeToHyperEVM' && !isDestination) {
    return asset.l1Balance
  }

  if (asset.l1Balance && (swapMode === 'buyHyperCore' || swapMode === 'sellHyperCore')) {
    return asset.l1Balance
  }

  return asset.balance || NormalizedUnitNumber(0)
}
